import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { LOBBY_BOARD_ICON_PATHS } from "./LobbyBoard3D";

// Second lobby design: the real 13x13 XIMA board (A-M, 1-13) with grass squares,
// and its walls work as buttons. No cubes on top, no coordinates on the walls.
const TEXTURE_BASE = "/design-studio/lobby-board";
const TAP_MAX_DISTANCE_PX = 7;
const TAP_MAX_DURATION_MS = 650;
const WALL_OFFSET = 0.012;

// Floors from the base to the G7 tower. Each upper floor rises two blocks.
const FLOORS = [
  { half: 6.5, bottom: 0, top: 0.5 },
  { half: 4.5, bottom: 0.5, top: 2.5 },
  { half: 2.5, bottom: 2.5, top: 4.5 },
  { half: 0.5, bottom: 4.5, top: 6.5 },
];

// Sides in order front (+z), right (+x), back (-z), left (-x).
const SIDES = [
  { normal: [0, 1], tangent: [1, 0], rotation: 0 },
  { normal: [1, 0], tangent: [0, -1], rotation: Math.PI / 2 },
  { normal: [0, -1], tangent: [-1, 0], rotation: Math.PI },
  { normal: [-1, 0], tangent: [0, 1], rotation: -Math.PI / 2 },
];

// Floor 2: four buttons per wall, covering it (jugar, competir, aprender, cuenta).
// Floor 3: one game mode per wall.
const SECOND_FLOOR_WALLS = [
  ["create", "find", "bot", "local"],
  ["championship", "tournaments", "puzzles", "friends"],
  ["learn", "history", "stats", "store"],
  ["design", "settings", "language", "support"],
];
const SECOND_FLOOR_BUTTON_GAP = 0.12;
const THIRD_FLOOR_WALLS = ["science", "cienzar", "chance", "contrazar"];
const PLAY_GREEN = "#22b85a";

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const loadIcon = (block) =>
  block?.image
    ? loadImage(block.image)
    : loadImage(
        `data:image/svg+xml;utf8,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="256" height="256" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${
            LOBBY_BOARD_ICON_PATHS[block?.iconKey] || ""
          }</svg>`,
        )}`,
      );

const darkenColor = (hex, amount) => {
  const value = parseInt(String(hex).replace("#", ""), 16);
  const channel = (shift) => Math.round(((value >> shift) & 255) * (1 - amount));
  return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
};

const makeCanvas = (width, height) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return [canvas, canvas.getContext("2d")];
};

const toTexture = (canvas) => {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
};

const paintButtonBackground = (context, width, height, color) => {
  const fill = context.createLinearGradient(0, 0, width, height);
  fill.addColorStop(0, color);
  fill.addColorStop(1, darkenColor(color, 0.42));
  context.fillStyle = fill;
  context.fillRect(0, 0, width, height);
  const shine = context.createRadialGradient(width * 0.3, height * 0.15, 10, width * 0.3, height * 0.15, Math.max(width, height) * 0.8);
  shine.addColorStop(0, "rgba(255,255,255,0.3)");
  shine.addColorStop(1, "rgba(255,255,255,0)");
  context.fillStyle = shine;
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "rgba(255,255,255,0.85)";
  context.lineWidth = 12;
  context.strokeRect(9, 9, width - 18, height - 18);
};

const wrapLabel = (context, text, maxWidth, maxLines) => {
  const lines = [];
  let current = "";
  String(text || "")
    .toUpperCase()
    .split(" ")
    .forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (context.measureText(candidate).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    });
  lines.push(current);
  return lines.slice(0, maxLines);
};

// Button of the second floor (a bit wider than tall): icon on top, name below.
const createSquareButtonTexture = (block, icon, fontFamily, aspect = 1) => {
  const height = 512;
  const width = Math.round(height * aspect);
  const [canvas, context] = makeCanvas(width, height);
  paintButtonBackground(context, width, height, block.color);
  if (icon) {
    context.shadowColor = "rgba(0,0,0,0.5)";
    context.shadowBlur = 14;
    context.drawImage(icon, (width - 230) / 2, 48, 230, 230);
  }
  context.fillStyle = "#ffffff";
  context.shadowColor = "rgba(0,0,0,0.7)";
  context.shadowBlur = 8;
  context.textAlign = "center";
  const fontSize = 64;
  context.font = `800 ${fontSize}px ${fontFamily}`;
  const lines = wrapLabel(context, block.label, width - 60, 2);
  lines.forEach((line, index) => {
    const y = 345 + fontSize + index * (fontSize + 6) - (lines.length - 1) * (fontSize / 2);
    context.fillText(line, width / 2, y, width - 40);
  });
  return toTexture(canvas);
};

// Whole wall of the third floor: mode logo and name.
const createModeWallTexture = (block, icon, fontFamily) => {
  const width = 1040;
  const height = 400;
  const [canvas, context] = makeCanvas(width, height);
  paintButtonBackground(context, width, height, block.color);
  const iconSize = 250;
  if (icon) {
    context.shadowColor = "rgba(0,0,0,0.5)";
    context.shadowBlur = 16;
    context.drawImage(icon, 70, (height - iconSize) / 2, iconSize, iconSize);
  }
  context.fillStyle = "#ffffff";
  context.shadowColor = "rgba(0,0,0,0.7)";
  context.shadowBlur = 10;
  context.textAlign = "left";
  context.textBaseline = "middle";
  let fontSize = 116;
  const label = String(block.label || "").toUpperCase();
  context.font = `900 ${fontSize}px ${fontFamily}`;
  const maxWidth = width - iconSize - 150;
  while (fontSize > 60 && context.measureText(label).width > maxWidth) {
    fontSize -= 4;
    context.font = `900 ${fontSize}px ${fontFamily}`;
  }
  context.fillText(label, iconSize + 110, height / 2, maxWidth);
  return toTexture(canvas);
};

// Tower wall: green, the word PLAY and a play symbol inside a circle.
const createPlayWallTexture = (fontFamily) => {
  const width = 256;
  const height = 512;
  const [canvas, context] = makeCanvas(width, height);
  paintButtonBackground(context, width, height, PLAY_GREEN);
  context.fillStyle = "#ffffff";
  context.shadowColor = "rgba(0,0,0,0.55)";
  context.shadowBlur = 10;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = `900 66px ${fontFamily}`;
  context.fillText("PLAY", width / 2, 150, width - 30);
  const centerX = width / 2;
  const centerY = 310;
  const radius = 92;
  context.shadowColor = "rgba(0,0,0,0.45)";
  context.shadowBlur = 12;
  context.strokeStyle = "#ffffff";
  context.lineWidth = 16;
  context.beginPath();
  context.arc(centerX, centerY, radius, 0, Math.PI * 2);
  context.stroke();
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.moveTo(centerX - 30, centerY - 46);
  context.lineTo(centerX + 50, centerY);
  context.lineTo(centerX - 30, centerY + 46);
  context.closePath();
  context.fill();
  return toTexture(canvas);
};

const LobbyBoardTower3D = ({ blocks, className = "", onReady, onUnavailable }) => {
  const mountRef = useRef(null);
  const blocksRef = useRef(blocks);
  const callbacksRef = useRef({ onReady, onUnavailable });
  blocksRef.current = blocks;
  callbacksRef.current = { onReady, onUnavailable };
  const blocksKey = blocks.map((block) => `${block.layoutKey}:${block.label}:${block.color}`).join("|");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (error) {
      callbacksRef.current.onUnavailable?.();
      return undefined;
    }

    let disposed = false;
    const ownedTextures = [];
    const ownedMaterials = [];
    const ownedGeometries = [];
    const own = (list, item) => {
      list.push(item);
      return item;
    };
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.className = "xima-lobby-board3d-canvas";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 200);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.7;
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.PAN,
    };
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.minDistance = 10;
    controls.maxDistance = 48;
    controls.maxPolarAngle = Math.PI * 0.47;
    controls.target.set(0, 2.6, 0);
    // Slower and smoother spin than the classic board.
    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    // Negative = turns to the left; slow so the walls can be read.
    const SPIN_SPEED = -0.38;
    let spin = prefersReducedMotion ? 0 : SPIN_SPEED;
    let spinTarget = spin;
    controls.autoRotate = !prefersReducedMotion;
    controls.autoRotateSpeed = spin;
    let autoRotateTimer = null;
    const handleControlsStart = () => {
      window.clearTimeout(autoRotateTimer);
      spinTarget = 0;
    };
    const handleControlsEnd = () => {
      window.clearTimeout(autoRotateTimer);
      if (prefersReducedMotion) return;
      autoRotateTimer = window.setTimeout(() => {
        spinTarget = SPIN_SPEED;
      }, 2500);
    };
    controls.addEventListener("start", handleControlsStart);
    controls.addEventListener("end", handleControlsEnd);

    scene.add(new THREE.HemisphereLight(0xfff1dd, 0x2a1a0c, 1.5));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.3);
    keyLight.position.set(9, 20, 13);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.bias = -0.0005;
    Object.assign(keyLight.shadow.camera, { left: -14, right: 14, top: 14, bottom: -14 });
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0xffc27a, 0.9);
    rimLight.position.set(-12, 8, -10);
    scene.add(rimLight);
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.7);
    fillLight.position.set(-6, 6, 14);
    scene.add(fillLight);

    const resize = () => {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const aspect = width / height;
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
      if (!camera.userData.placed) {
        const distance = aspect >= 1.4 ? 22 : aspect >= 1 ? 25 : 31;
        const direction = new THREE.Vector3(0.18, 0.5, 1).normalize();
        camera.position.copy(controls.target).addScaledVector(direction, distance);
        camera.userData.placed = true;
        controls.update();
      }
    };
    resize();
    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(mount);

    const buttons = [];
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered = null;
    let pointerStart = null;

    const pickButton = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
      // Walls of the board block buttons behind them.
      const hit = raycaster.intersectObjects(scene.children, true).find((entry) => entry.object.visible);
      return hit && hit.object.userData.layoutKey ? hit.object : null;
    };
    const handleMouseDown = (event) => {
      if (event.button === 1) event.preventDefault();
    };
    const handlePointerDown = (event) => {
      pointerStart = event.button === 0 ? { x: event.clientX, y: event.clientY, time: performance.now() } : null;
    };
    const handlePointerUp = (event) => {
      if (!pointerStart) return;
      const moved = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y);
      const elapsed = performance.now() - pointerStart.time;
      pointerStart = null;
      if (moved > TAP_MAX_DISTANCE_PX || elapsed > TAP_MAX_DURATION_MS) return;
      const button = pickButton(event);
      if (!button) return;
      const block = blocksRef.current.find((entry) => entry.layoutKey === button.userData.layoutKey);
      block?.onClick?.();
    };
    const handlePointerMove = (event) => {
      if (event.pointerType !== "mouse") return;
      hovered = pickButton(event);
      renderer.domElement.style.cursor = hovered ? "pointer" : "grab";
    };
    const handleContextMenu = (event) => event.preventDefault();
    const handlePointerLeave = () => {
      hovered = null;
    };
    // Phones in portrait: once the board cannot tilt any further, the same
    // vertical drag scrolls the page so the buttons below stay reachable.
    const portraitPhoneQuery = window.matchMedia?.("(max-width: 860px) and (orientation: portrait)");
    const findScrollContainer = () => {
      let element = mount.parentElement;
      while (element && element !== document.body) {
        const overflowY = window.getComputedStyle(element).overflowY;
        if ((overflowY === "auto" || overflowY === "scroll") && element.scrollHeight > element.clientHeight) {
          return element;
        }
        element = element.parentElement;
      }
      return null;
    };
    let lastTouch = null;
    const handleTouchStart = (event) => {
      lastTouch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    };
    const handleTouchMove = (event) => {
      if (!lastTouch || event.touches.length !== 1 || !portraitPhoneQuery?.matches) {
        lastTouch = null;
        return;
      }
      const touch = event.touches[0];
      const deltaX = touch.clientX - lastTouch.x;
      const deltaY = touch.clientY - lastTouch.y;
      lastTouch = { x: touch.clientX, y: touch.clientY };
      if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
      const polar = controls.getPolarAngle();
      const tiltedToBottom = polar >= controls.maxPolarAngle - 0.03 && deltaY < 0;
      const tiltedToTop = polar <= controls.minPolarAngle + 0.03 && deltaY > 0;
      if (!tiltedToBottom && !tiltedToTop) return;
      const container = findScrollContainer();
      if (container) container.scrollBy(0, -deltaY);
      else window.scrollBy(0, -deltaY);
    };
    renderer.domElement.addEventListener("touchstart", handleTouchStart, { passive: true });
    renderer.domElement.addEventListener("touchmove", handleTouchMove, { passive: true });
    renderer.domElement.addEventListener("mousedown", handleMouseDown);
    renderer.domElement.addEventListener("contextmenu", handleContextMenu);
    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    renderer.domElement.addEventListener("pointerup", handlePointerUp);
    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("pointerleave", handlePointerLeave);

    let visible = true;
    const intersectionObserver =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            visible = entries.some((entry) => entry.isIntersecting);
          })
        : null;
    intersectionObserver?.observe(mount);

    const panLimit = new THREE.Box3(new THREE.Vector3(-7, -1, -7), new THREE.Vector3(7, 9, 7));
    const clampedTarget = new THREE.Vector3();
    renderer.setAnimationLoop(() => {
      if (!visible) return;
      // Ease the spin in and out instead of stopping or starting at once.
      spin += (spinTarget - spin) * 0.025;
      controls.autoRotate = Math.abs(spin) > 0.01;
      controls.autoRotateSpeed = spin;
      controls.update();
      clampedTarget.copy(controls.target).clamp(panLimit.min, panLimit.max);
      if (!clampedTarget.equals(controls.target)) {
        camera.position.add(clampedTarget.clone().sub(controls.target));
        controls.target.copy(clampedTarget);
      }
      buttons.forEach((button) => {
        const goal = button === hovered ? 0.28 : 0;
        button.material.emissiveIntensity += (goal - button.material.emissiveIntensity) * 0.2;
      });
      renderer.render(scene, camera);
    });

    const loadTexture = (url) =>
      new THREE.TextureLoader().loadAsync(url).then(
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.anisotropy = 8;
          return own(ownedTextures, texture);
        },
        () => null,
      );

    const build = async () => {
      const [lightTexture, darkTexture, wallTexture] = await Promise.all([
        loadTexture(`${TEXTURE_BASE}/casilla-clara.jpg`),
        loadTexture(`${TEXTURE_BASE}/casilla-oscura.jpg`),
        loadTexture(`${TEXTURE_BASE}/pared.jpg`),
      ]);
      if (disposed) return;

      const board = new THREE.Group();
      board.name = "Tablero XIMA";
      const grout = own(ownedMaterials, new THREE.MeshStandardMaterial({ color: "#1f2a14", roughness: 0.9 }));

      // Floors: plain wood walls, the top is covered by grass squares.
      FLOORS.forEach((floor, floorIndex) => {
        const size = floor.half * 2;
        const height = floor.top - floor.bottom;
        let sideMap = null;
        if (wallTexture) {
          sideMap = own(ownedTextures, wallTexture.clone());
          sideMap.repeat.set(size / 2, height / 2);
          sideMap.needsUpdate = true;
        }
        const side = own(
          ownedMaterials,
          new THREE.MeshStandardMaterial({
            color: floorIndex === 3 ? PLAY_GREEN : sideMap ? "#ffffff" : "#4a2c17",
            map: floorIndex === 3 ? null : sideMap,
            roughness: 0.62,
            metalness: 0.04,
          }),
        );
        const geometry = own(ownedGeometries, new THREE.BoxGeometry(size, height, size));
        const mesh = new THREE.Mesh(geometry, [side, side, grout, grout, side, side]);
        mesh.position.set(0, floor.bottom + height / 2, 0);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        board.add(mesh);
      });

      // Grass squares on the open surface of every floor. Light when col+row is even.
      const topAt = (x, z) => {
        for (let index = FLOORS.length - 1; index >= 0; index -= 1) {
          if (Math.abs(x) <= FLOORS[index].half && Math.abs(z) <= FLOORS[index].half) return FLOORS[index].top;
        }
        return FLOORS[0].top;
      };
      const tileGeometry = own(ownedGeometries, new THREE.PlaneGeometry(0.96, 0.96));
      tileGeometry.rotateX(-Math.PI / 2);
      const squares = { light: [], dark: [] };
      for (let row = 0; row < 13; row += 1) {
        for (let col = 0; col < 13; col += 1) {
          const x = col - 6;
          const z = 6 - row;
          squares[(col + row) % 2 === 0 ? "light" : "dark"].push([x, topAt(x, z) + 0.002, z]);
        }
      }
      const dummy = new THREE.Object3D();
      [
        ["light", lightTexture, "#7fbf4d"],
        ["dark", darkTexture, "#3f7a2a"],
      ].forEach(([kind, map, fallback]) => {
        const material = own(
          ownedMaterials,
          new THREE.MeshStandardMaterial({ color: map ? "#ffffff" : fallback, map, roughness: 0.85 }),
        );
        const tiles = new THREE.InstancedMesh(tileGeometry, material, squares[kind].length);
        squares[kind].forEach(([x, y, z], index) => {
          dummy.position.set(x, y, z);
          dummy.updateMatrix();
          tiles.setMatrixAt(index, dummy.matrix);
        });
        tiles.receiveShadow = true;
        board.add(tiles);
      });

      // Wall buttons.
      const fontFamily = window.getComputedStyle(document.body).fontFamily || "sans-serif";
      const blockFor = (layoutKey) => blocksRef.current.find((block) => block.layoutKey === layoutKey);
      const addWallButton = ({ floor, sideIndex, along, width, height, texture, layoutKey }) => {
        const sideInfo = SIDES[sideIndex];
        const geometry = own(ownedGeometries, new THREE.PlaneGeometry(width, height));
        const material = own(
          ownedMaterials,
          new THREE.MeshStandardMaterial({
            map: own(ownedTextures, texture),
            roughness: 0.4,
            metalness: 0.05,
            emissive: new THREE.Color("#ffffff"),
            emissiveIntensity: 0,
          }),
        );
        const mesh = new THREE.Mesh(geometry, material);
        const distance = floor.half + WALL_OFFSET;
        mesh.position.set(
          sideInfo.normal[0] * distance + sideInfo.tangent[0] * along,
          (floor.bottom + floor.top) / 2,
          sideInfo.normal[1] * distance + sideInfo.tangent[1] * along,
        );
        mesh.rotation.y = sideInfo.rotation;
        mesh.userData.layoutKey = layoutKey;
        mesh.receiveShadow = true;
        mesh.name = blockFor(layoutKey)?.label || layoutKey;
        buttons.push(mesh);
        board.add(mesh);
      };

      const secondFloorKeys = SECOND_FLOOR_WALLS.flat();
      const icons = await Promise.all(
        [...secondFloorKeys, ...THIRD_FLOOR_WALLS].map((key) => loadIcon(blockFor(key))),
      );
      if (disposed) return;
      const iconFor = (key) => icons[[...secondFloorKeys, ...THIRD_FLOOR_WALLS].indexOf(key)];

      SECOND_FLOOR_WALLS.forEach((keys, sideIndex) => {
        // Buttons share the whole wall, a little wider than tall, so the wood barely shows.
        const wallWidth = FLOORS[1].half * 2;
        const buttonWidth = (wallWidth - SECOND_FLOOR_BUTTON_GAP * (keys.length + 1)) / keys.length;
        const buttonHeight = FLOORS[1].top - FLOORS[1].bottom - 0.16;
        keys.forEach((layoutKey, slot) => {
          const block = blockFor(layoutKey);
          if (!block) return;
          addWallButton({
            floor: FLOORS[1],
            sideIndex,
            along: -wallWidth / 2 + SECOND_FLOOR_BUTTON_GAP + buttonWidth / 2 + slot * (buttonWidth + SECOND_FLOOR_BUTTON_GAP),
            width: buttonWidth,
            height: buttonHeight,
            texture: createSquareButtonTexture(block, iconFor(layoutKey), fontFamily, buttonWidth / buttonHeight),
            layoutKey,
          });
        });
      });
      THIRD_FLOOR_WALLS.forEach((layoutKey, sideIndex) => {
        const block = blockFor(layoutKey);
        if (!block) return;
        addWallButton({
          floor: FLOORS[2],
          sideIndex,
          along: 0,
          width: 4.82,
          height: 1.86,
          texture: createModeWallTexture(block, iconFor(layoutKey), fontFamily),
          layoutKey,
        });
      });
      SIDES.forEach((_, sideIndex) => {
        addWallButton({
          floor: FLOORS[3],
          sideIndex,
          along: 0,
          width: 0.98,
          height: 1.96,
          texture: createPlayWallTexture(fontFamily),
          layoutKey: "play",
        });
      });

      scene.add(board);
      mount.dataset.boardReady = "true";
      callbacksRef.current.onReady?.();
    };
    build();

    return () => {
      disposed = true;
      renderer.setAnimationLoop(null);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      renderer.domElement.removeEventListener("mousedown", handleMouseDown);
      renderer.domElement.removeEventListener("contextmenu", handleContextMenu);
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerleave", handlePointerLeave);
      renderer.domElement.removeEventListener("touchstart", handleTouchStart);
      renderer.domElement.removeEventListener("touchmove", handleTouchMove);
      window.clearTimeout(autoRotateTimer);
      controls.removeEventListener("start", handleControlsStart);
      controls.removeEventListener("end", handleControlsEnd);
      controls.dispose();
      ownedTextures.forEach((texture) => texture.dispose());
      ownedMaterials.forEach((material) => material.dispose());
      ownedGeometries.forEach((geometry) => geometry.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      delete mount.dataset.boardReady;
    };
  }, [blocksKey]);

  return <div ref={mountRef} className={`xima-lobby-board3d is-tower ${className}`.trim()} />;
};

export default LobbyBoardTower3D;
